<template>
  <nav v-if="total > 0" :aria-label="$t('common.pagination')"
       class="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
    <p class="text-sm text-gray-600" aria-live="polite">
      {{ $t('common.showing_results', { from, to, total }) }}
    </p>

    <div v-if="lastPage > 1" class="flex items-center gap-1">
      <button type="button" :disabled="loading || currentPage <= 1" @click="goTo(currentPage - 1)"
              :aria-label="$t('common.previous')" :title="$t('common.previous')"
              class="inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 transition hover:bg-gray-50 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-bpblue focus:ring-offset-2">
        <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd"/>
        </svg>
      </button>

      <!-- En móvil solo «página X de Y»; los números a partir de sm -->
      <span class="px-3 text-sm text-gray-700 sm:hidden">
        {{ $t('common.page_of', { page: currentPage, pages: lastPage }) }}
      </span>
      <template v-for="(item, index) in pages" :key="item === gap ? 'gap-' + index : item">
        <span v-if="item === gap" class="hidden h-9 w-9 items-center justify-center text-sm text-gray-500 sm:inline-flex" aria-hidden="true">…</span>
        <button v-else type="button" :disabled="loading" @click="goTo(item)"
                :aria-current="item === currentPage ? 'page' : null"
                :aria-label="$t('common.go_to_page', { page: item })"
                :class="item === currentPage
                  ? 'border-bpblue bg-bpblue text-white'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'"
                class="hidden h-9 min-w-[2.25rem] items-center justify-center rounded-md border px-2 text-sm font-medium tabular-nums transition active:scale-[0.98] disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-bpblue focus:ring-offset-2 sm:inline-flex">
          {{ item }}
        </button>
      </template>

      <button type="button" :disabled="loading || currentPage >= lastPage" @click="goTo(currentPage + 1)"
              :aria-label="$t('common.next')" :title="$t('common.next')"
              class="inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 transition hover:bg-gray-50 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-bpblue focus:ring-offset-2">
        <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"/>
        </svg>
      </button>
    </div>
  </nav>
</template>

<script>
const GAP = 'gap'

/*
 * Controles de paginación genéricos para respuestas paginadas de Laravel
 * (`meta.current_page`, `meta.last_page`, `meta.from`, `meta.to`, `meta.total`).
 * No navega por sí mismo: emite `change` con la página pedida.
 */
export default {
  name: "PaginationControls",
  props: {
    currentPage: { type: Number, required: true },
    lastPage: { type: Number, required: true },
    total: { type: Number, required: true },
    from: { type: Number, default: 0 },
    to: { type: Number, default: 0 },
    loading: { type: Boolean, default: false },
  },
  emits: ['change'],
  data() {
    return { gap: GAP }
  },
  computed: {
    // Primera, última y las vecinas de la actual; el resto se resume con «…».
    pages() {
      const last = this.lastPage
      const current = this.currentPage
      const wanted = new Set([1, last, current - 1, current, current + 1])
      const pages = [...wanted].filter((page) => page >= 1 && page <= last).sort((a, b) => a - b)

      return pages.reduce((items, page, index) => {
        if (index > 0 && page - pages[index - 1] > 1) {
          items.push(page - pages[index - 1] === 2 ? page - 1 : GAP)
        }
        items.push(page)
        return items
      }, [])
    },
  },
  methods: {
    goTo(page) {
      if (page < 1 || page > this.lastPage || page === this.currentPage) {
        return
      }
      this.$emit('change', page)
    },
  },
}
</script>
